import { useEffect, useRef, useState } from 'react';
import {
  TimeSheetPrefetchDataFragment,
  TimeSheetPrefetchDataFragmentDoc,
  useTimeSheetDetailsPrefetchLazyQuery,
} from '../../__generated__/graphql';
import { TableRow, TableRowTimeSheet, isTableRowTimeSheet } from './types';
import { useApolloCache } from '../../hooks/useApolloCache';
import { ApolloCache } from '@apollo/client';

// Each time we get new timeSheets from server (at firstRequest and on every fetchMore)
// we prefetch timeSheet details which are required for showing ChartsPopover and expanding days
// The code will work without it, but it hugly improves the UI responsiveness
export const useTimeSheetDetailsPrefetch = (
  tableRows: TableRow[],
  withTimeEntryExtraFields = false,
) => {
  const cache = useApolloCache();
  const notLoadedSubRowsTimeSheetIds = tableRows
    .filter(
      (x): x is TableRowTimeSheet =>
        isTableRowTimeSheet(x) &&
        !isSubRowsLoaded(cache, x.timeSheetId, withTimeEntryExtraFields),
    )
    .map((x) => x.timeSheetId);

  const [prefetchTimeSheets] = useTimeSheetDetailsPrefetchLazyQuery();
  const [prefetchedIds, setPrefetchedIds] = useState<string[]>([]);
  const [inProgress, setInProgress] = useState<boolean>(false);

  const withExtraFieldsRef = useRef(withTimeEntryExtraFields);
  withExtraFieldsRef.current = withTimeEntryExtraFields;

  // The flag starts false while extra-field metadata loads, so early prefetches
  // run without extraFields. When it flips, forget those ids so they are
  // re-prefetched with the extra-field selection (isSubRowsLoaded already sees
  // the cached rows as incomplete).
  useEffect(() => {
    setPrefetchedIds([]);
  }, [withTimeEntryExtraFields]);

  const idsToPrefetch = notLoadedSubRowsTimeSheetIds.filter(
    (x) => !prefetchedIds.includes(x),
  );

  useEffect(() => {
    if (inProgress || !idsToPrefetch.length) {
      return;
    }
    setInProgress(true);
    prefetchTimeSheets({
      variables: {
        ids: idsToPrefetch,
        withTimeEntryExtraFields,
      },
    })
      .then((queryResult) => {
        // If the extra-fields flag flipped while this request was in flight, its
        // result is stale (fetched with the old selection). Discarding it leaves
        // the ids out of prefetchedIds so they get re-prefetched with the new flag.
        if (withExtraFieldsRef.current !== withTimeEntryExtraFields) {
          return;
        }
        // it is more safe to use queryResult.variables because I spotted that sometimes prefetchTimeSheets could be canceled
        // and the idsToPrefetch will contains wrong values from the first canceled request
        const prefetchedIds = queryResult.variables?.ids;
        if (!prefetchedIds) {
          return;
        }
        setPrefetchedIds((prev) => [...prev, ...prefetchedIds]);
      })
      .finally(() => {
        setInProgress(false);
      });
  }, [
    idsToPrefetch,
    prefetchTimeSheets,
    setPrefetchedIds,
    inProgress,
    setInProgress,
    withTimeEntryExtraFields,
  ]);
};

const isSubRowsLoaded = (
  cache: ApolloCache<object>,
  timeSheetId: string,
  withTimeEntryExtraFields: boolean,
): boolean => {
  // there is a room for optimization there, we can store already loaded id
  const timeSheetCacheId: string | undefined = cache.identify({
    __typename: 'TimeSheet',
    Id: timeSheetId,
  });
  if (!timeSheetCacheId) {
    return false;
  }
  const cacheRes = cache.readFragment<TimeSheetPrefetchDataFragment>({
    id: timeSheetCacheId,
    fragment: TimeSheetPrefetchDataFragmentDoc,
    fragmentName: 'TimeSheetPrefetchData',
    // the fragment gates time-entry extraFields behind @include(if:), so the
    // variable must be supplied or Apollo throws while reading the cache
    variables: { withTimeEntryExtraFields },
  });
  return cacheRes !== null;
};
